/**
 * Stops a `type="number"` input from editing itself.
 *
 * A number input spins on the mouse wheel whenever it has focus, and on
 * ArrowUp/ArrowDown. Neither gesture means "change this figure" in this app:
 *
 *  - **The wheel means "scroll what I am reading."** It does not stop at the
 *    field. A clerk who clicks into an amount on the collection desk and then
 *    scrolls the drawer to reach Save turns ₹12,500 into ₹12,507 on the way
 *    past. Nothing flags it — the field did exactly what it was told — and the
 *    figure is wrong on a receipt that has already been handed over.
 *  - **The arrows mean "move."** ±1 is not a useful step for a rupee amount or
 *    a marks total, and someone arrowing through a form expects to travel, not
 *    to edit.
 *
 * Both are browser defaults, so nothing in the app has to be wrong for this to
 * happen. That is what makes it worth blocking in the primitives: it is these
 * two functions, or the same silent corruption on every amount in the product.
 *
 * `type="number"` itself is kept — it carries the numeric keypad on a tablet at
 * the counter, and the `min`/`step` constraints the fee and expense forms rely
 * on. It is only the spin gesture that is unwanted.
 */

/**
 * Blur, rather than `preventDefault()`.
 *
 * Both stop the spin, but blurring also lets the wheel through to the drawer or
 * table underneath, so the page still scrolls the way the user was asking it
 * to. Swallowing the event instead would trade a silently wrong amount for a
 * container that looks frozen — and they were scrolling *away* from the field,
 * so the focus is no loss.
 *
 * It has to be the blur that runs first, and it does: the increment is the
 * event's default action, which the browser performs after dispatch, by which
 * point the field is no longer focused and it is skipped. This is also why an
 * `onWheel` prop is enough here — nothing needs `preventDefault()`, which React
 * could not deliver anyway, since it registers `wheel` at the root as passive.
 */
export function blockWheelSpin(e: React.WheelEvent<HTMLInputElement>) {
  if (document.activeElement === e.currentTarget) e.currentTarget.blur();
}

/** Pair with the above. Compose after any handler the caller passed. */
export function blockArrowSpin(e: React.KeyboardEvent<HTMLInputElement>) {
  if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault();
}
